/**
 * City × specialty listing SEO experiment (ghane DR, owner approval 2026-09-18).
 *
 * Google shows our listing 4th–5th for "<specialty> <big city>" while nobat.ir / doctoreto lead with
 * «بهترین دکتر … <شهر>». This page already has what they don't: live first free slots and review counts.
 * For routes on the GrowthBook allow-list only, the title/description are rebuilt from the live result,
 * and valid BreadcrumbList + ItemList JSON-LD are emitted (the CMS `jsonld` is a bare {href,name} array
 * with no @context/@type, so Google cannot read it). Everything else keeps the CMS template untouched,
 * so the experiment is a page split: treated routes vs matched control routes, compared in Search Console.
 */

type SeoCrumb = { href?: string; name?: string };
type SearchAction = { title?: string; top_title?: string };
type SearchCard = {
  type?: string;
  title?: string;
  prefix?: string;
  url?: string;
  rates_count?: number;
  actions?: SearchAction[];
};

export type ListingSeoExperiment = {
  title: string;
  description: string;
  jsonlds: object[];
};

const ORIGIN = 'https://www.paziresh24.com';
const faNumber = (n: number) => n.toLocaleString('fa-IR');
const stripHtml = (s: string) => s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

/** Path of a listing URL without query/hash, always with a trailing slash. */
export const listingPath = (asPath: string) => {
  const path = asPath.split(/[?#]/)[0];
  return path.endsWith('/') ? path : `${path}/`;
};

/** First free in-person slot text on the page («امروز …» beats «فردا …» beats any date). */
export const earliestSlot = (result: SearchCard[] = []): string | null => {
  const slots = result
    .flatMap(card => card.actions ?? [])
    .filter(action => action.title?.startsWith('نوبت') && action.top_title)
    .map(action => stripHtml(action.top_title as string))
    .filter(text => text.startsWith('اولین نوبت'))
    .map(text => text.replace(/^اولین نوبت\s*:\s*/, ''));
  return slots.find(s => s.startsWith('امروز')) ?? slots.find(s => s.startsWith('فردا')) ?? slots[0] ?? null;
};

export const buildListingSeoExperiment = ({
  asPath,
  routes,
  crumbs,
  total,
  result,
}: {
  asPath: string;
  routes: Array<string | null>;
  crumbs: SeoCrumb[] | undefined;
  total: number | undefined;
  result: SearchCard[] | undefined;
}): ListingSeoExperiment | null => {
  if (asPath.includes('?')) return null; // filtered / paginated / text views keep the CMS template
  const path = listingPath(asPath);
  if (!routes.some(route => route && listingPath(route) === path)) return null;

  const city = crumbs?.[1]?.name?.trim();
  const group = crumbs?.[crumbs.length - 1]?.name?.trim();
  if (!city || !group || !crumbs || crumbs.length < 3 || !total) return null;

  const cards = result ?? [];
  const slot = earliestSlot(cards);
  const soonDay = slot?.match(/^(امروز|فردا)/)?.[1];
  const reviews = cards.reduce((sum, card) => sum + (card.rates_count ?? 0), 0);

  const title = `بهترین دکتر ${group} ${city}، نوبت اینترنتی${soonDay ? ` از ${soonDay}` : ''}`;
  const description = [
    `${faNumber(total)} پزشک ${group} در ${city}`,
    slot ? `اولین نوبت خالی: ${slot}` : null,
    reviews > 0 ? `بیش از ${faNumber(reviews)} نظر بیماران` : null,
    'مقایسه و نوبت‌دهی آنلاین در پذیرش۲۴',
  ]
    .filter(Boolean)
    .join(' · ');

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.href,
    })),
  };
  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `بهترین دکتر ${group} ${city}`,
    numberOfItems: total,
    itemListElement: cards
      .filter(card => card.type === 'doctor' && card.url)
      .slice(0, 10)
      .map((card, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${ORIGIN}${encodeURI(decodeURI(card.url as string))}`,
        name: [card.prefix, card.title].filter(Boolean).join(' '),
      })),
  };

  return { title, description, jsonlds: [breadcrumbList, itemList] };
};

export default buildListingSeoExperiment;
